/**
 * CR317: S0 Scan Settings v2 - Large Pages Tab
 * Downscale controls, safety floor, tiling threshold settings
 */

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Maximize2, ChevronDown, Grid3X3, AlertTriangle } from 'lucide-react';
import type { BatchRunFlyerConfig, LargePagesConfig } from '../types';

interface LargePagesTabProps {
  config: BatchRunFlyerConfig;
  updateConfig: <K extends keyof BatchRunFlyerConfig>(key: K, value: BatchRunFlyerConfig[K]) => void;
  disabled?: boolean;
}

export function LargePagesTab({
  config,
  updateConfig,
  disabled = false,
}: LargePagesTabProps) {
  const { largePages, tiledScanning } = config;
  
  const updateLargePages = (updates: Partial<LargePagesConfig>) => {
    updateConfig('largePages', {
      ...largePages,
      ...updates,
    });
  };

  const updateDownscale = (updates: Partial<LargePagesConfig['downscale']>) => {
    updateLargePages({
      downscale: {
        ...largePages.downscale,
        ...updates,
      },
    });
  };

  const updateDownscaleApplyWhen = (updates: Partial<LargePagesConfig['downscale']['applyWhen']>) => {
    updateDownscale({
      applyWhen: {
        ...largePages.downscale.applyWhen,
        ...updates,
      },
    });
  };

  const updateTiling = (updates: Partial<LargePagesConfig['tiling']>) => {
    updateLargePages({
      tiling: {
        ...largePages.tiling,
        ...updates,
      },
    });
  };

  const updateTilingApplyWhen = (updates: Partial<LargePagesConfig['tiling']['applyWhen']>) => {
    updateTiling({
      applyWhen: {
        ...largePages.tiling.applyWhen,
        ...updates,
      },
    });
  };

  // Compute effective target for preview
  const effectiveTargetMax = Math.max(
    largePages.downscale.maxDimensionPx,
    largePages.downscale.minOutputMaxDimensionPx
  );
  const floorActive = largePages.downscale.maxDimensionPx < largePages.downscale.minOutputMaxDimensionPx;

  return (
    <div className="space-y-6">
      {/* Downscale Section */}
      <Collapsible defaultOpen className="space-y-3">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-foreground transition-colors w-full group">
          <Maximize2 className="h-4 w-4" />
          <span>Downscale Strategy</span>
          <Badge variant={largePages.downscale.enabled ? "outline" : "secondary"} className="ml-2 text-xs">
            {largePages.downscale.enabled ? "On" : "Off"}
          </Badge>
          <ChevronDown className="h-4 w-4 ml-auto transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4 pt-2 pl-6 border-l-2 border-muted">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs">Enable Downscaling</Label>
              <p className="text-xs text-muted-foreground">
                Reduce large page dimensions before OCR to improve accuracy and memory usage
              </p>
            </div>
            <Switch
              checked={largePages.downscale.enabled}
              onCheckedChange={(v) => updateDownscale({ enabled: v })}
              disabled={disabled}
            />
          </div>

          {largePages.downscale.enabled && (
            <>
              {/* Mode */}
              <div className="space-y-2">
                <Label className="text-xs">Downscale Mode</Label>
                <Select
                  value={largePages.downscale.mode}
                  onValueChange={(v) => updateDownscale({ mode: v as 'max_dimension' | 'megapixels' })}
                  disabled={disabled}
                >
                  <SelectTrigger className="h-8 text-xs w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="max_dimension">Max Dimension (px)</SelectItem>
                    <SelectItem value="megapixels">Megapixels (area)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Max Dimension */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Max Dimension (px)</Label>
                  <Badge variant="outline" className="text-xs font-mono">
                    {largePages.downscale.maxDimensionPx}
                  </Badge>
                </div>
                <Input
                  type="number"
                  min={800}
                  max={4000}
                  step={100}
                  value={largePages.downscale.maxDimensionPx}
                  onChange={(e) => updateDownscale({ maxDimensionPx: parseInt(e.target.value) || 2200 })}
                  disabled={disabled}
                  className="h-8 text-xs w-32"
                />
                <p className="text-xs text-muted-foreground">
                  Target output max dimension after downscaling (800–4000)
                </p>
              </div>

              {/* Safety Floor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Minimum Output Max Dimension (px)</Label>
                  <Badge variant="outline" className="text-xs font-mono">
                    {largePages.downscale.minOutputMaxDimensionPx}
                  </Badge>
                </div>
                <Input
                  type="number"
                  min={600}
                  max={2400}
                  step={100}
                  value={largePages.downscale.minOutputMaxDimensionPx}
                  onChange={(e) => updateDownscale({ minOutputMaxDimensionPx: parseInt(e.target.value) || 1200 })}
                  disabled={disabled}
                  className="h-8 text-xs w-32"
                />
                <p className="text-xs text-muted-foreground">
                  Prevents downscaling below this max dimension to preserve OCR quality.
                </p>
              </div>

              {/* Apply When Threshold */}
              <div className="space-y-3 pt-2 border-t border-dashed">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs">Apply Only When Threshold Met</Label>
                    <p className="text-xs text-muted-foreground">
                      Only downscale if source dimension exceeds threshold
                    </p>
                  </div>
                  <Switch
                    checked={largePages.downscale.applyWhen.enabled}
                    onCheckedChange={(v) => updateDownscaleApplyWhen({ enabled: v })}
                    disabled={disabled}
                  />
                </div>

                {largePages.downscale.applyWhen.enabled && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Min Source Dimension (px)</Label>
                    <Input
                      type="number"
                      min={1000}
                      max={6000}
                      step={100}
                      value={largePages.downscale.applyWhen.minDimensionPx}
                      onChange={(e) => updateDownscaleApplyWhen({ minDimensionPx: parseInt(e.target.value) || 2600 })}
                      disabled={disabled}
                      className="h-8 text-xs w-32"
                    />
                  </div>
                )}
              </div>

              {/* Effective Config Preview */}
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <Label className="text-xs font-medium">Effective Downscale Config</Label>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">mode:</span>
                    <span>{largePages.downscale.mode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">targetMax:</span>
                    <span>{largePages.downscale.maxDimensionPx}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">floor:</span>
                    <span>{largePages.downscale.minOutputMaxDimensionPx}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">effectiveMax:</span>
                    <span className={floorActive ? "text-amber-600" : ""}>{effectiveTargetMax}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">threshold:</span>
                    <span>{largePages.downscale.applyWhen.enabled ? `≥${largePages.downscale.applyWhen.minDimensionPx}` : "always"}</span>
                  </div>
                  {floorActive && (
                    <div className="col-span-2 flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      <span>Floor active: targetMax clamped up</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Tiling Section */}
      <Collapsible defaultOpen className="space-y-3">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-foreground transition-colors w-full group">
          <Grid3X3 className="h-4 w-4" />
          <span>Tiled Scanning</span>
          <Badge variant={largePages.tiling.enabled ? "outline" : "secondary"} className="ml-2 text-xs">
            {largePages.tiling.enabled ? "On" : "Off"}
          </Badge>
          <ChevronDown className="h-4 w-4 ml-auto transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4 pt-2 pl-6 border-l-2 border-muted">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs">Enable Tiled Scanning</Label>
              <p className="text-xs text-muted-foreground">
                Split large pages into overlapping tiles for better OCR accuracy
              </p>
            </div>
            <Switch
              checked={largePages.tiling.enabled}
              onCheckedChange={(v) => updateTiling({ enabled: v })}
              disabled={disabled}
            />
          </div>

          {largePages.tiling.enabled && (
            <>
              {/* Tile Size & Overlap (uses existing tiledScanning config) */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tile Size (px)</Label>
                  <Input
                    type="number"
                    min={512}
                    max={4096}
                    step={128}
                    value={tiledScanning.tileSizePx}
                    onChange={(e) => updateConfig('tiledScanning', {
                      ...tiledScanning,
                      tileSizePx: parseInt(e.target.value) || 1536
                    })}
                    disabled={disabled}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tile Overlap (px)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={512}
                    step={32}
                    value={tiledScanning.tileOverlapPx}
                    onChange={(e) => updateConfig('tiledScanning', {
                      ...tiledScanning,
                      tileOverlapPx: parseInt(e.target.value) || 192
                    })}
                    disabled={disabled}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              {/* Apply When Threshold */}
              <div className="space-y-3 pt-2 border-t border-dashed">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs">Apply Only When Threshold Met</Label>
                    <p className="text-xs text-muted-foreground">
                      Only tile if source dimension exceeds threshold
                    </p>
                  </div>
                  <Switch
                    checked={largePages.tiling.applyWhen.enabled}
                    onCheckedChange={(v) => updateTilingApplyWhen({ enabled: v })}
                    disabled={disabled}
                  />
                </div>

                {largePages.tiling.applyWhen.enabled && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Min Source Dimension (px)</Label>
                    <Input
                      type="number"
                      min={1000}
                      max={6000}
                      step={100}
                      value={largePages.tiling.applyWhen.minDimensionPx}
                      onChange={(e) => updateTilingApplyWhen({ minDimensionPx: parseInt(e.target.value) || 2600 })}
                      disabled={disabled}
                      className="h-8 text-xs w-32"
                    />
                  </div>
                )}
              </div>

              {/* Effective Config Preview */}
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <Label className="text-xs font-medium">Effective Tiling Config</Label>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">tileSize:</span>
                    <span>{tiledScanning.tileSizePx}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">overlap:</span>
                    <span>{tiledScanning.tileOverlapPx}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">threshold:</span>
                    <span>{largePages.tiling.applyWhen.enabled ? `≥${largePages.tiling.applyWhen.minDimensionPx}` : "always"}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
